import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

/**
 * This is the conventional Picture component for CDN images.
 * All it currently does is ensure consistent aspect-ratio handling,
 * breakpoint handling, and source conversion.
 * NOTE: Currently we're not adding support for different file formats,
 * or preloading.
 */
import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import useNativeLazyLoading from '@charlietango/use-native-lazy-loading';
import { useInView } from 'react-intersection-observer';
import { useCarouselContext } from '../CarouselContext';
import { useGridState } from '../GridProvider';
import { toMediaQuery } from '../useBreakpointSelector';
import { parseAspectRatio } from '../utils/images';
var ImageElement = styled.img.withConfig({
  displayName: "Picture__ImageElement",
  componentId: "sc-nrybnt-0"
})(["\n  width: 100%;\n  height: auto;\n  display: block;\n\n  ", "\n"], props => props.aspectRatio && props.aspectRatio !== 'auto' && css(["\n      object-fit: ", ";\n\n      &:before {\n        display: block;\n        content: '';\n        width: 100%;\n        padding-top: ", "%;\n      }\n    "], props.objectFit || 'cover', 100 / Number(props.aspectRatio)));
var PictureElement = styled.picture.withConfig({
  displayName: "Picture__PictureElement",
  componentId: "sc-nrybnt-1"
})(["\n  display: block;\n  width: 100%;\n"]); // NOTE: The breakpoint format allows us to pass a raw mediaQuery string,
// which means we'll have to make a best effort to determine the max-width sometimes

var mediaQueryMaxWidthRe = /max-width:\s+(\d+)px/;
var mediaQueryMinWidthRe = /min-width:\s+(\d+)px/;

var getMaxWidth = (breakpoint, nextBreakpoint) => {
  var match;

  if (breakpoint.maxWidth) {
    return breakpoint.maxWidth;
  } else if (nextBreakpoint && nextBreakpoint.minWidth) {
    return nextBreakpoint.minWidth - 1;
  } else if (breakpoint.mediaQuery && (match = breakpoint.mediaQuery.match(mediaQueryMaxWidthRe))) {
    return parseInt(match[1], 10);
  } else if (nextBreakpoint && nextBreakpoint.mediaQuery && (match = nextBreakpoint.mediaQuery.match(mediaQueryMinWidthRe))) {
    return parseInt(match[1], 10);
  }

  return null;
};

var useSizes = (breakpoints, containerMaxWidth) => {
  var grid = useGridState();
  var breakpointsToUse = breakpoints || (grid === null || grid === void 0 ? void 0 : grid.breakpoints) || [];
  return useMemo(() => {
    var pictureBreakpoints = breakpointsToUse.map((breakpoint, i, array) => {
      var nextBreakpoint = array[i + 1];
      var pictureWidth = '';

      if (breakpoint.pictureWidth) {
        pictureWidth = "".concat(breakpoint.pictureWidth, "px");
      } else if (breakpoint.columns) {
        var maxWidth = getMaxWidth(breakpoint, nextBreakpoint) || containerMaxWidth || null;
        var gapsWidth = typeof breakpoint.gap === 'number' ? breakpoint.gap * (breakpoint.columns - 1) : 0;

        if (maxWidth !== null) {
          pictureWidth = "".concat((maxWidth - gapsWidth) / breakpoint.columns, "px");
        } else {
          // NOTE: If we don't have an overall `maxWidth` then we assume that the Picture
          // will fill the viewport eventually
          pictureWidth = gapsWidth ? "calc((100vw - ".concat(gapsWidth, "px) / ").concat(breakpoint.columns, ")") : "calc(100vw / ".concat(breakpoint.columns, ")");
        }
      }

      return pictureWidth ? "".concat(toMediaQuery(breakpoint, nextBreakpoint), " ").concat(pictureWidth) : '';
    }).filter(Boolean);

    if (!pictureBreakpoints.length) {
      throw new Error('Could not generate Picture sizes.' + ' <Picture> must either be placed inside a <Grid> with valid max-width information' + ' or must be passed a `breakpoints` list with `pictureWidth` attributes.');
    }

    return pictureBreakpoints.join(', ');
  }, [breakpointsToUse, containerMaxWidth]);
};
/**
 * Each image from the CDN, by convention, carries the image's URL
 * and width.
 */


var Picture = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var sources = _ref.sources,
      aspectRatioFromProps = _ref.aspectRatio,
      objectFit = _ref.objectFit,
      breakpoints = _ref.breakpoints,
      containerMaxWidth = _ref.containerMaxWidth,
      hidden = _ref.hidden,
      style = _ref.style,
      className = _ref.className,
      imgStyle = _ref.imgStyle,
      onLoad = _ref.onLoad,
      _ref$loading = _ref.loading,
      loading = _ref$loading === void 0 ? 'eager' : _ref$loading,
      _ref$rootMargin = _ref.rootMargin,
      rootMargin = _ref$rootMargin === void 0 ? '400px' : _ref$rootMargin,
      props = _objectWithoutProperties(_ref, ["sources", "aspectRatio", "objectFit", "breakpoints", "containerMaxWidth", "hidden", "style", "className", "imgStyle", "onLoad", "loading", "rootMargin"]);

  var carousel = useCarouselContext();
  var lazyLoad = loading === 'lazy';
  var supportsLazyLoading = useNativeLazyLoading();

  var _useInView = useInView({
    triggerOnce: true,
    rootMargin,
    skip: !lazyLoad || supportsLazyLoading
  }),
      _useInView2 = _slicedToArray(_useInView, 2),
      imageRef = _useInView2[0],
      imageIsInView = _useInView2[1];

  var loadImage = supportsLazyLoading || imageIsInView || !lazyLoad;
  var handleLoad = useCallback(function () {
    if (carousel) {
      carousel.notifyImageLoaded();
    }

    if (onLoad) {
      onLoad(...arguments);
    }
  }, [carousel, onLoad]);
  var aspectRatio = useMemo(() => {
    return typeof aspectRatioFromProps === 'string' ? parseAspectRatio(aspectRatioFromProps) : aspectRatioFromProps;
  }, [aspectRatioFromProps]);
  var sizes = useSizes(breakpoints, containerMaxWidth);

  if (!sources.length) {
    return null;
  }

  var pictureSources = sources.map((source, index) => {
    var srcSet = source.images.map(image => "".concat(image.url, " ").concat(image.width, "w")).join(', ');
    return /*#__PURE__*/React.createElement("source", {
      srcSet: srcSet,
      sizes: sizes,
      type: source.type,
      key: index
    });
  }); // First image of the last source set, so that fallback URL is `.jpg` or
  // whatever the lowest priority (probably most compatible) image is.

  var fallback = sources[sources.length - 1].images[0];
  return /*#__PURE__*/React.createElement(PictureElement, {
    style: style,
    className: className,
    hidden: hidden,
    key: fallback.url,
    onLoad: handleLoad,
    ref: imageRef
  }, loadImage ? pictureSources : null, loadImage && fallback ? /*#__PURE__*/React.createElement(ImageElement // Allow explicit props to override width and height. Note that
  // these won't set the actual rendered size since it is overridden
  // with CSS above, but it does give the browser an idea of the
  // aspect ratio while the image is still loading *if* both width and
  // height are actually defined, while the :before pseudo-element
  // does not.
  , _extends({
    width: fallback.width,
    height: fallback.height,
    loading: loading
  }, props, {
    style: imgStyle || style,
    objectFit: objectFit,
    aspectRatio: aspectRatio,
    className: className,
    ref: ref,
    onLoad: handleLoad,
    src: fallback.url
  })) : null);
});
Picture.displayName = 'Picture';
export default Picture;